import React, { type ReactNode } from 'react';

import { DOMAIN, REASON, reportError } from '~/services/telemetry';

type Props = {
  parent?: string;
  children: ReactNode;
};

interface Error {
  stack?: string;
}

export default class CategoryBlockErrorBoundary extends React.Component<
  Props,
  { hasError: boolean }
> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // Check if we're on the server
    if (typeof window === 'undefined') {
      console.error(`[SSR Error][${this.props.parent}]:`, error);
      return;
    }

    // The stack used to be interpolated into the title, so every distinct
    // stack opened its own issue and the same broken block could never be
    // totalled up. Reported as an exception it groups once, with the block in
    // the fingerprint so different blocks stay apart.
    reportError(DOMAIN.ui, error, {
      reason: REASON.ui.blockRenderFailed,
      fingerprint: [this.props.parent ?? 'unknown'],
      extras: { parent: this.props.parent ?? 'unknown' },
    });
  }

  render() {
    if (this.state.hasError) {
      // Return null or a fallback UI for SSR
      return null;
    }

    return this.props.children;
  }
}
